import * as THREE from 'three';
import type { Material } from 'three';
import type { ManifestFile } from '../worlds/manifest.js';
import { displayed } from './sky-parts.js';

/**
 * A lesson's models get their own soft light in a dark world (ADR-100).
 *
 * In "Dinosaurussen in de sneeuw ontdekken" the T. rex stands in the northern lights' night, lit by a
 * faint aurora and the stars, and reads as a silhouette (Pieter, 3 Nov 2026: "Ik zie vooral de
 * contouren van de dino"). So the headset works out, from what the world's package already says and
 * the platform measured of its sky, how much light falls on a model now -- on the side the sun
 * lights, and on the side only the sky lights -- and, only as far as that is too little, gives the
 * lesson's library models a key light from the front and side as the pupil sees them and a fill from
 * the pupil's side. In a daylight world both are exactly 0, so a model looks as it did.
 *
 * It lights the models and nothing else: not a three.js light (which every lit material in the world
 * would pay for and be lit by, the scenery too), but two terms added to the models' own materials'
 * lighting (`litByModelLight`). No shadows, no extra draws, no world material recompiled.
 */
export const MODEL_LIGHT = {
  /** The key at full need, as a directional light's intensity (the daylight sun is 2–4). */
  key: 1.2,
  /** The fill at full need, as an ambient light's (irradiance; the studio's sky light is 0.5). */
  fill: 0.45,
  /** A touch cool, as moonlight and starlight: a lamp's warm white would read as a torch. */
  colour: 0xe6ecff,
  /**
   * The lit side's light (the sun's share and the sky's, `lightOnModels`): below `litDark` the key
   * is at full, from `litOk` none. Daylight worlds measure 0.6–1.6, the northern lights 0.1–0.15.
   */
  litDark: 0.15,
  litOk: 0.45,
  /**
   * The shaded side's light (the sky's alone): below `shadeDark` the fill is at full, from
   * `shadeOk` none. Daylight and indoor worlds measure 0.25–1.2, space and night worlds under 0.03.
   */
  shadeDark: 0.03,
  shadeOk: 0.12,
  /** Seconds the light takes to follow a world's state that darkens or lightens it (about 95 % in 3×). */
  ease: 0.8,
  /** Where the key comes from: this far round from the pupil's side of the model, to their right (as the studio's)… */
  turn: 40,
  /** …and this far up. */
  rise: 35,
  /** How far the key wraps round a shape (0 is Lambert's hard terminator): soft, never a flash. */
  wrap: 0.4,
  /** A fully metal surface's share of the light, in its own colour, against a matte one's. */
  metal: 0.5,
} as const;

/**
 * The mean radiance of the studio's room (three's RoomEnvironment through PMREM, intensity 1), which
 * a world's light falls back to when its sky cannot be had (look.ts): a white matte sphere in it
 * shows 1.9 facing the room's lights, 1.4 from above, 1.0 from the side (measured in Chrome, 3 Nov 2026).
 */
export const ROOM_RADIANCE = 1.4;

/** A world's light now, as the model light reads it. */
export type WorldLight = {
  /** The sun's intensity now: the definition's times its light blocks' share. */
  readonly sun: number;
  /** The sky light's intensity now (`scene.environmentIntensity`): the definition's times the share. */
  readonly environment: number;
  /** The sky's mean radiance (linear luminance), as measured; undefined when nobody measured it. */
  readonly sky: number | undefined;
  /** The renderer's exposure (the world's `sky.exposure`). */
  readonly exposure: number;
};

/** How much light falls on a model, as a share of its albedo seen: on its sunlit side and its shaded side. */
export type LightOnModels = { readonly lit: number; readonly shaded: number };

/** How much of the model light is needed, 0 to 1, for the key and the fill. */
export type ModelLightNeed = { readonly key: number; readonly fill: number };

export const NO_NEED: ModelLightNeed = { key: 0, fill: 0 };

const luminance = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

const linearOf = (srgb: number): number =>
  srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;

/** The brightest radiance the inversion answers: a colour shown as white could be anything above. */
const BRIGHTEST = 64;

/**
 * The sky radiance (linear, grey) that the media worker's `displayed` (three's ACES, exposure 1, then
 * sRGB) shows as this colour's luminance: the inverse of what measured a package's sky.
 */
export function radianceShownAs(hex: string): number {
  const colour = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!colour) return Number.NaN;
  const n = Number.parseInt(colour[1]!, 16);
  const target = luminance(
    linearOf(((n >> 16) & 255) / 255),
    linearOf(((n >> 8) & 255) / 255),
    linearOf((n & 255) / 255),
  );
  const shown = (x: number): number => {
    const [r, g, b] = displayed(x, x, x, 1);
    return luminance(linearOf(r), linearOf(g), linearOf(b));
  };
  if (target <= 0) return 0;
  if (target >= shown(BRIGHTEST)) return BRIGHTEST;
  let low = 0;
  let high = BRIGHTEST;
  for (let i = 0; i < 48; i++) {
    const mid = (low + high) / 2;
    if (shown(mid) < target) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

/**
 * A sky's mean radiance, from its horizon's and zenith's measured colours: the import's linear values
 * for a platform sky (manifest.json), else the worker's displayed colours inverted (a package's own
 * sky). Undefined when neither was measured.
 */
export function skyRadiance(
  sky: Pick<ManifestFile, 'horizon' | 'zenith' | 'horizonLinear' | 'zenithLinear'>,
): number | undefined {
  const linear = [sky.horizonLinear, sky.zenithLinear].filter(
    (c): c is readonly number[] => c !== undefined && c.length >= 3,
  );
  if (linear.length > 0) {
    return linear.reduce((sum, c) => sum + luminance(c[0]!, c[1]!, c[2]!), 0) / linear.length;
  }
  const shown = [sky.horizon, sky.zenith]
    .filter((c): c is string => typeof c === 'string')
    .map(radianceShownAs)
    .filter((v) => Number.isFinite(v));
  return shown.length > 0 ? shown.reduce((a, b) => a + b, 0) / shown.length : undefined;
}

/**
 * How much light falls on a model now. three's lights (r155 on): a directional light of intensity I
 * shows a surface facing it at albedo × I / π; a sky of radiance L at intensity E lights every side at
 * about albedo × E × L. Both times the exposure.
 */
export function lightOnModels(world: WorldLight): LightOnModels | undefined {
  if (world.sky === undefined) return undefined;
  const shaded = world.environment * world.sky * world.exposure;
  return { lit: (world.sun / Math.PI) * world.exposure + shaded, shaded };
}

const UP = new THREE.Vector3(0, 1, 0);

/** 1 below `dark`, 0 from `ok`, smooth between. */
const shortfall = (value: number, dark: number, ok: number): number =>
  1 - THREE.MathUtils.smoothstep(value, dark, ok);

/**
 * How much model light a world needs now: the key as far as its lit side is too dim, the fill as far
 * as its shaded side is. A sky nobody measured (a package's own, its measure failed or older) is
 * taken as black for the key alone -- its sun is all that is known to light a model, and the dimmest
 * daylight sun (1.6) still clears `litOk` -- and gives no fill: a black guess would fill every such
 * world, daylight too.
 */
export function modelLightNeed(world: WorldLight): ModelLightNeed {
  const light = lightOnModels(world);
  if (!light) {
    const lit = lightOnModels({ ...world, sky: 0 })!.lit;
    return { key: shortfall(lit, MODEL_LIGHT.litDark, MODEL_LIGHT.litOk), fill: 0 };
  }
  return {
    key: shortfall(light.lit, MODEL_LIGHT.litDark, MODEL_LIGHT.litOk),
    fill: shortfall(light.shaded, MODEL_LIGHT.shadeDark, MODEL_LIGHT.shadeOk),
  };
}

/**
 * Where the key comes from for a model whose middle is `centre`, seen from `eye` (unit, world space):
 * from the pupil's side, `turn` degrees round to the pupil's right and `rise` degrees up. It follows the
 * pupil round the model, never their head's turning.
 */
export function keyDirection(
  centre: THREE.Vector3,
  eye: THREE.Vector3,
  into = new THREE.Vector3(),
): THREE.Vector3 {
  into.set(eye.x - centre.x, 0, eye.z - centre.z);
  if (into.lengthSq() < 1e-8) into.set(0, 0, 1);
  into.normalize();
  // Round +y by `turn`: from the pupil's side towards their right.
  into.applyAxisAngle(UP, THREE.MathUtils.degToRad(MODEL_LIGHT.turn));
  const rise = THREE.MathUtils.degToRad(MODEL_LIGHT.rise);
  into.multiplyScalar(Math.cos(rise));
  into.y = Math.sin(rise);
  return into.normalize();
}

/** The uniforms a patched material reads: the strengths (shared) and its model's key direction (its own). */
export type ModelLightUniforms = {
  readonly modelKey: THREE.IUniform<THREE.Color>;
  readonly modelFill: THREE.IUniform<THREE.Color>;
  readonly modelKeyDirection: THREE.IUniform<THREE.Vector3>;
};

const FRAGMENT_HEAD = /* glsl */ `
uniform vec3 modelKey;
uniform vec3 modelFill;
uniform vec3 modelKeyDirection;
`;

// After the world's lights (lights_fragment_end), before the ambient occlusion (aomap_fragment), so
// a model's own crevices stay dark. The key wraps (half-soft) and adds no highlight; the fill is
// strongest on what faces the pupil. A metal has no diffuse colour to light (diffuseContribution is
// its colour × (1 − metalness)), so it gets the same two terms in its own colour, at `metal` of a
// matte surface's, as a soft sheen: a metal rocket must not stay a silhouette (ADR-100, fix round 1).
const FRAGMENT_LIGHT = /* glsl */ `
#include <lights_fragment_end>
{
  vec3 modelKeyView = normalize( ( viewMatrix * vec4( modelKeyDirection, 0.0 ) ).xyz );
  float modelKeyNL = saturate( ( dot( geometryNormal, modelKeyView ) + ${MODEL_LIGHT.wrap.toFixed(2)} ) / ${(1 + MODEL_LIGHT.wrap).toFixed(2)} );
  float modelFacing = 0.5 + 0.5 * dot( geometryNormal, geometryViewDir );
  vec3 modelLit = modelKeyNL * modelKey + modelFacing * modelFill;
  reflectedLight.directDiffuse += ( modelKeyNL * modelKey ) * BRDF_Lambert( material.diffuseContribution );
  reflectedLight.indirectDiffuse += ( modelFacing * modelFill ) * BRDF_Lambert( material.diffuseContribution );
  reflectedLight.indirectSpecular += modelLit * material.specularColorBlended * ( ${MODEL_LIGHT.metal.toFixed(2)} * metalnessFactor * RECIPROCAL_PI );
}
`;

/**
 * What the patch needs of three's shader code, by chunk: the names it reads must be there, or a
 * three upgrade that renamed one would fail every patched model's program. Checked once.
 */
const NEEDS: readonly (readonly [chunk: string, name: string])[] = [
  ['lights_fragment_begin', 'geometryNormal'],
  ['lights_fragment_begin', 'geometryViewDir'],
  ['lights_physical_fragment', 'material.diffuseContribution'],
  ['lights_physical_fragment', 'material.specularColorBlended'],
  ['metalnessmap_fragment', 'metalnessFactor'],
  ['common', 'RECIPROCAL_PI'],
  ['common', 'BRDF_Lambert'],
];

let chunksFit: boolean | undefined;
let warned = false;

/** Whether this three's chunks have every name the patch reads (cached). */
export function chunksHaveNames(
  chunks: Readonly<Record<string, string>> = THREE.ShaderChunk,
): boolean {
  if (chunks === THREE.ShaderChunk && chunksFit !== undefined) return chunksFit;
  const fit = NEEDS.every(([chunk, name]) => chunks[chunk]?.includes(name) === true);
  if (chunks === THREE.ShaderChunk) chunksFit = fit;
  return fit;
}

/**
 * The fragment shader with the model light in, or undefined when it cannot go in: a place it goes
 * (`void main() {`, `#include <lights_fragment_end>`) is not there once, or a name it reads is not in
 * three's chunks.
 */
export function withModelLight(
  fragmentShader: string,
  chunks: Readonly<Record<string, string>> = THREE.ShaderChunk,
): string | undefined {
  const once = (text: string): boolean => fragmentShader.split(text).length === 2;
  if (!once('void main() {') || !once('#include <lights_fragment_end>')) return undefined;
  if (!chunksHaveNames(chunks)) return undefined;
  return fragmentShader
    .replace('void main() {', `${FRAGMENT_HEAD}\nvoid main() {`)
    .replace('#include <lights_fragment_end>', FRAGMENT_LIGHT);
}

/** The key every patched program shares (the code is the same for every model). */
const PROGRAM_KEY = 'model-light-2';

/**
 * Patches a model's material to take the model light: a standard or physical material (what glTF
 * gives); an unlit one (KHR_materials_unlit) and anything else are left alone. Returns whether it
 * was patched. A material's own earlier patch runs first.
 */
export function litByModelLight(material: THREE.Material, uniforms: ModelLightUniforms): boolean {
  if (!(material instanceof THREE.MeshStandardMaterial)) return false;
  if (material.userData['modelLight']) return true;
  material.userData['modelLight'] = true;
  // The material's own patch, if it has one (read off the material, never three's default).
  const ownPatch = Object.getOwnPropertyDescriptor(material, 'onBeforeCompile')?.value as
    Material['onBeforeCompile'] | undefined;
  const own = ownPatch
    ? (shader: THREE.WebGLProgramParametersWithUniforms, renderer: THREE.WebGLRenderer): void =>
        ownPatch.call(material, shader, renderer)
    : undefined;
  const ownKey = Object.hasOwn(material, 'customProgramCacheKey')
    ? material.customProgramCacheKey.bind(material)
    : undefined;
  // three's own default key is the patch's source: kept, so two different own patches never share a program.
  const before = ownKey ?? (ownPatch ? () => ownPatch.toString() : () => '');
  material.onBeforeCompile = (shader, renderer) => {
    own?.(shader, renderer);
    const patched = withModelLight(shader.fragmentShader);
    if (patched === undefined) {
      // Left as three made it: unlit by the model light, never broken.
      if (!warned) {
        warned = true;
        console.warn(
          'model light: this three.js shader has no place for it; models are left as they are',
        );
      }
      return;
    }
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = patched;
  };
  material.customProgramCacheKey = () => `${before()}|${PROGRAM_KEY}`;
  material.needsUpdate = true;
  return true;
}

/** Close enough to its need to be put there: half a percent of the light is not seen. */
const ARRIVED = 0.005;

type Lit = {
  readonly holder: THREE.Object3D;
  /** The model's middle in the holder's own space. */
  readonly middle: THREE.Vector3;
  readonly direction: THREE.IUniform<THREE.Vector3>;
};

/**
 * The model light of one lesson in a world (ADR-100): the strengths every model shares, eased to the
 * world's need, and each model's own key direction, kept on the pupil's side of it.
 */
export class ModelLight {
  readonly #key: THREE.IUniform<THREE.Color> = { value: new THREE.Color(0, 0, 0) };
  readonly #fill: THREE.IUniform<THREE.Color> = { value: new THREE.Color(0, 0, 0) };
  readonly #colour = new THREE.Color(MODEL_LIGHT.colour);
  readonly #models: Lit[] = [];
  readonly #centre = new THREE.Vector3();
  #now: ModelLightNeed = NO_NEED;
  #started = false;

  /** How much of each light is on now, 0 to 1 (tests, the HUD). */
  get now(): ModelLightNeed {
    return this.#now;
  }

  /** The models it lights. */
  get count(): number {
    return this.#models.length;
  }

  /**
   * Lights a fitted model: its materials patched, its middle `height / 2` above its holder's origin
   * (its feet, in the holder's units).
   */
  attach(holder: THREE.Object3D, materials: readonly THREE.Material[], height: number): void {
    const direction: THREE.IUniform<THREE.Vector3> = { value: new THREE.Vector3(0, 1, 0) };
    const uniforms = { modelKey: this.#key, modelFill: this.#fill, modelKeyDirection: direction };
    let patched = false;
    for (const material of materials) patched = litByModelLight(material, uniforms) || patched;
    if (patched) {
      this.#models.push({ holder, middle: new THREE.Vector3(0, height / 2, 0), direction });
    }
  }

  /** Lets go of a model (its materials keep the patch, at whatever they last read). */
  detach(holder: THREE.Object3D): void {
    const at = this.#models.findIndex((m) => m.holder === holder);
    if (at >= 0) this.#models.splice(at, 1);
  }

  /**
   * A frame: the strengths eased towards `need` (at once on the first frame, and while the view is
   * `black` -- fully, not while a fade only darkens it), and every model's key turned to where the
   * pupil's `eye` is now.
   */
  update(dt: number, need: ModelLightNeed, eye: THREE.Vector3, black = false): void {
    if (!this.#started || black) {
      this.#now = need;
      this.#started = true;
    } else {
      const k = 1 - Math.exp(-Math.max(0, dt) / MODEL_LIGHT.ease);
      const ease = (from: number, to: number): number =>
        Math.abs(to - from) < ARRIVED ? to : from + (to - from) * k;
      this.#now = { key: ease(this.#now.key, need.key), fill: ease(this.#now.fill, need.fill) };
    }
    this.#key.value.copy(this.#colour).multiplyScalar(MODEL_LIGHT.key * this.#now.key);
    this.#fill.value.copy(this.#colour).multiplyScalar(MODEL_LIGHT.fill * this.#now.fill);
    if (this.#now.key === 0) return;
    for (const model of this.#models) {
      this.#centre.copy(model.middle);
      model.holder.localToWorld(this.#centre);
      keyDirection(this.#centre, eye, model.direction.value);
    }
  }

  dispose(): void {
    this.#models.length = 0;
  }
}
